import { relinkUserVault, unlinkUserVault, updateUserVaults, VaultSets } from '@/app/actions/user-vaults'
import {
  GetUserAchievementsDocument,
  GetUserAchievementsQuery,
  GetUserProfileDocument,
  GetUserTokensDocument,
  GetUserTokensQuery,
  UpdateUserProfileDocument,
  UserProfileFragment
} from '@/gql/generated/graphql'
import { AllSetsProgressResponse } from '@artblocks/shared-types'
import { Client } from '@urql/core'
import { fetchAllSetsProgress } from '@utils/sets'
import { createStore } from '@xstate/store'
import { Session } from 'next-auth'
import { UpdateSession } from 'next-auth/react'
import { Hex } from 'viem'
import { UsePublicClientReturnType } from 'wagmi'

export const ALL_ONBOARDING_STEPS = [
  'WELCOME',
  'CHOOSE_PATH',
  'LINK_ACCOUNTS',
  'PROFILE_SETUP',
  'CONFIRMATION'
] as const

export type Step = (typeof ALL_ONBOARDING_STEPS)[number]

type OnboardingUserData = {
  profile: UserProfileFragment | null
  achievements: GetUserAchievementsQuery['user_achievements'] | null
  sets: AllSetsProgressResponse | null
  tokens: GetUserTokensQuery | null
  delegations: VaultSets
}

const initialContext = {
  open: false,
  urqlClient: null as Client | null,
  publicClient: null as UsePublicClientReturnType | null,
  session: null as Session | null,
  updateSession: null as UpdateSession | null,
  activeSteps: ALL_ONBOARDING_STEPS as unknown as Step[],
  signUpType: null as 'wallet' | 'email' | null,
  step: ALL_ONBOARDING_STEPS[0] as Step,
  // User accounts linked to privy - provided by privy hook
  userAccounts: [] as Array<Hex>,
  // User data from the database
  userDataLoading: false,
  profile: null as UserProfileFragment | null,
  achievements: null as GetUserAchievementsQuery['user_achievements'] | null,
  sets: null as AllSetsProgressResponse | null,
  tokens: null as GetUserTokensQuery | null,
  // Retrieved from core api
  delegations: null as VaultSets | null,
  // Vault loading state
  delegationsUpdating: [] as Array<string>,
  isRefreshingDelegations: false
}

export const onboardingStore = createStore({
  context: initialContext,
  on: {
    reset: () => {
      return initialContext
    },
    open: (ctx, event: { signUpType: 'wallet' | 'email' }, enqueue) => {
      const activeSteps =
        event.signUpType === 'wallet'
          ? ALL_ONBOARDING_STEPS.filter((step) => step !== 'CHOOSE_PATH')
          : (ALL_ONBOARDING_STEPS as unknown as Step[])

      // When the onboarding modal is opened, we want to update the user onboarding seen flag
      // so that we don't automatically show it again. If the user has not completed onboarding,
      // they should be able to open it again via a banner on their profile page.
      enqueue.effect(async () => {
        const { urqlClient, session, updateSession } = ctx

        if (!urqlClient || !session || !updateSession) {
          return
        }

        const profileId = session.user?.profileId
        if (profileId) {
          try {
            await updateUserOnboardingSeen(profileId, urqlClient)
            await updateSession({
              ...session,
              user: {
                ...session.user,
                onboardingSeen: true
              }
            })
          } catch (error) {
            console.error('Failed to update user onboarding seen', error)
          }
        }
      })

      return {
        ...ctx,
        open: true,
        signUpType: event.signUpType,
        activeSteps
      }
    },
    close: (ctx) => {
      return {
        ...ctx,
        open: false
      }
    },
    externalContextUpdated: (
      ctx,
      event: {
        session: Session | null
        updateSession: UpdateSession | null
        publicClient: UsePublicClientReturnType | null
        urqlClient: Client | null
      }
    ) => {
      return {
        ...ctx,
        session: event.session,
        updateSession: event.updateSession,
        publicClient: event.publicClient,
        urqlClient: event.urqlClient
      }
    },
    back: (ctx) => {
      if (ctx.step === 'WELCOME') {
        return {
          ...ctx,
          open: false
        }
      }

      const currentStepIndex = ctx.activeSteps.indexOf(ctx.step)
      return {
        ...ctx,
        step: ctx.activeSteps[currentStepIndex - 1]
      }
    },
    skipStep: (ctx) => {
      const currentStepIndex = ctx.activeSteps.indexOf(ctx.step)
      return {
        ...ctx,
        step: ctx.activeSteps[currentStepIndex + 1]
      }
    },
    welcomeStepCompleted: (ctx, _event, enqueue) => {
      const { urqlClient, publicClient, session } = ctx
      const profileId = session?.user?.profileId

      if (!profileId || !urqlClient || !publicClient) {
        return ctx
      }

      enqueue.effect(async () => {
        try {
          const [profile, achievements, sets, tokens, delegations] = await Promise.all([
            getUserProfile(profileId, urqlClient),
            getUserAchievements(profileId, urqlClient),
            fetchAllSetsProgress(profileId.toString()),
            getUserTokens(profileId, urqlClient),
            updateUserVaults()
          ])

          onboardingStore.send({
            type: 'userDataLoaded',
            userData: {
              profile,
              achievements: achievements.user_achievements ?? null,
              sets,
              tokens,
              delegations
            }
          })
        } catch (error) {
          console.error('Failed to fetch user data, returning to welcome', error)
          onboardingStore.trigger.errorFetchingUserData()
        }
      })

      const nextStepIndex = ctx.activeSteps.indexOf(ctx.step) + 1
      const nextStep = ctx.activeSteps[nextStepIndex]

      return {
        ...ctx,
        userDataLoading: true,
        step: nextStep
      }
    },
    errorFetchingUserData: (ctx) => {
      return {
        ...ctx,
        step: 'WELCOME' as const
      }
    },
    choosePathStepCompleted: (ctx, event: { nextStep: 'LINK_ACCOUNTS' | 'PROFILE_SETUP' }) => {
      return {
        ...ctx,
        step: event.nextStep
      }
    },
    linkAccountsStepCompleted: (ctx) => {
      return {
        ...ctx,
        step: 'PROFILE_SETUP' as const
      }
    },
    profileSetupStepCompleted: (ctx, event: { profile: UserProfileFragment }) => {
      return {
        ...ctx,
        profile: event.profile,
        step: 'CONFIRMATION' as const
      }
    },
    confirmationStepCompleted: (ctx, _event, enqueue) => {
      const { urqlClient, session, updateSession } = ctx
      const profileId = ctx.profile?.id

      if (!profileId || !urqlClient || !session || !updateSession) {
        return ctx
      }

      enqueue.effect(async () => {
        try {
          await updateUserOnboardingCompleted(profileId, urqlClient)
          await updateSession({
            ...session,
            user: {
              ...session.user,
              onboardingCompleted: true
            }
          })
        } catch (error) {
          console.error('Failed to update user onboarding completed', error)
        }
      })

      return {
        ...ctx,
        open: false
      }
    },
    userDataLoaded: (ctx, event: { userData: OnboardingUserData }) => {
      return {
        ...ctx,
        profile: event.userData.profile,
        achievements: event.userData.achievements,
        sets: event.userData.sets,
        tokens: event.userData.tokens,
        delegations: event.userData.delegations,
        userDataLoading: false
      }
    },
    linkVault: (ctx, event: { vault: string }, enqueue) => {
      enqueue.effect(async () => {
        await relinkUserVault(event.vault)
        onboardingStore.send({
          type: 'refreshDelegations'
        })
      })

      return {
        ...ctx,
        delegationsUpdating: [...ctx.delegationsUpdating, event.vault]
      }
    },
    unlinkVault: (ctx, event: { vault: string }, enqueue) => {
      enqueue.effect(async () => {
        await unlinkUserVault(event.vault)
        onboardingStore.send({
          type: 'refreshDelegations'
        })
      })
      return {
        ...ctx,
        delegationsUpdating: [...ctx.delegationsUpdating, event.vault]
      }
    },
    delegationsRefreshed: (ctx, event: { delegations: VaultSets }) => {
      return {
        ...ctx,
        delegations: event.delegations,
        delegationsUpdating: []
      }
    },
    refreshDelegations: (_ctx, _event, enqueue) => {
      enqueue.effect(async () => {
        const delegations = await updateUserVaults()

        onboardingStore.send({
          type: 'delegationsRefreshed',
          delegations
        })
      })
    },
    userAccountsUpdated: (ctx, event: { userAccounts: Array<Hex> }) => {
      return {
        ...ctx,
        userAccounts: event.userAccounts
      }
    },
    userProfileUpdated: (ctx, event: { profile: UserProfileFragment }) => {
      return {
        ...ctx,
        profile: event.profile
      }
    }
  }
})

async function getUserProfile(profileId: number, urqlClient: Client) {
  const { data, error } = await urqlClient
    .query(GetUserProfileDocument, {
      where: { id: { _eq: profileId } }
    })
    .toPromise()

  if (!data || error) {
    throw new Error('Failed to fetch user profile')
  }

  return data.user_profiles[0]
}

async function getUserAchievements(profileId: number, urqlClient: Client) {
  const { data, error } = await urqlClient
    .query(GetUserAchievementsDocument, {
      where: { user_profile_id: { _eq: profileId } }
    })
    .toPromise()

  if (!data || error) {
    throw new Error('Failed to fetch user achievements')
  }

  return data
}

async function getUserTokens(profileId: number, urqlClient: Client) {
  const { data, error } = await urqlClient
    .query(GetUserTokensDocument, {
      where: { owner: { profile_id: { _eq: profileId } } },
      limit: 12
    })
    .toPromise()

  if (!data || error) {
    throw new Error('Failed to fetch user tokens')
  }

  return data
}

async function updateUserOnboardingCompleted(profileId: number, urqlClient: Client) {
  const { data, error } = await urqlClient
    .mutation(UpdateUserProfileDocument, {
      pk: profileId,
      updates: {
        onboarding_completed: true
      }
    })
    .toPromise()

  if (!data || error) {
    throw new Error('Failed to update user onboarding completed')
  }

  return data
}

async function updateUserOnboardingSeen(profileId: number, urqlClient: Client) {
  const { data, error } = await urqlClient
    .mutation(UpdateUserProfileDocument, {
      pk: profileId,
      updates: {
        onboarding_seen: true
      }
    })
    .toPromise()

  if (!data || error) {
    throw new Error('Failed to update user onboarding seen')
  }

  return data
}
